import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { Button, Chip } from '@mui/material';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Appbar from 'components/appbar/Appbar';
import ConfirmDialog from 'components/confirm-dialog/ConfirmDialog';
import InsideLoading from 'components/loading/InsideLoading';
import InsideSaving from 'components/loading/InsideSaving';
import NoData from 'components/no-data/NoData';
import {
  ContentPad,
  PageShell,
  RecordFooter,
  RecordHeader,
  StyledForm,
  WorkArea,
} from 'components/record-shell/RecordShell';
import { api } from 'services/api';
import history from 'services/history';
import { PaymentMethod } from 'types/paymentMethod';
import PaymentMethodForm, { PaymentMethodErrors, validatePaymentMethod } from '../form/PaymentMethodForm';

const EditPaymentMethod: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [errors, setErrors] = useState<PaymentMethodErrors>({});
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null);
  const [saved, setSaved] = useState<PaymentMethod | null>(null);

  const loadPaymentMethod = useCallback(() => {
    if (!id) return Promise.resolve();

    return api
      .get<PaymentMethod>(`/payment-methods/${id}`)
      .then(response => {
        const data = response.data;
        const loaded = {
          id: data.id,
          name: data.name,
          active: data.active,
          max_installments: data.max_installments,
          type: data.type,
        };
        setPaymentMethod(loaded);
        setSaved(loaded);
      })
      .catch(err => {
        console.error(err);
        toast.error('Não foi possível carregar os dados da forma de recebimento');
        history.push('/payment-methods');
      });
  }, [id]);

  useEffect(() => {
    setLoading(true);
    loadPaymentMethod().finally(() => setLoading(false));
  }, [loadPaymentMethod]);

  function goBack() {
    history.push('/payment-methods');
  }

  function handleChange(index: keyof PaymentMethod, value: string | number) {
    setPaymentMethod(prev => (prev ? { ...prev, [index]: value } : null));
  }

  function handleSubmit() {
    if (!paymentMethod) return;

    setSaving(true);

    api
      .put(`/payment-methods/${paymentMethod.id}`, paymentMethod)
      .then(() => loadPaymentMethod())
      .then(() => toast.success('Forma de recebimento atualizada'))
      .catch(err => {
        toast.error(err.response?.data?.error || 'Não foi possível atualizar a forma de recebimento');
      })
      .finally(() => setSaving(false));
  }

  function handleValidation(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    if (!paymentMethod) return;

    const found = validatePaymentMethod(paymentMethod);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    handleSubmit();
  }

  // "excluir" só inativa: guias e pagamentos antigos continuam apontando para a forma
  function handleInactivate() {
    if (!paymentMethod) return;

    setSaving(true);

    api
      .delete(`/payment-methods/${paymentMethod.id}`)
      .then(() => {
        toast.success('Forma de recebimento inativada com sucesso!');
        history.push('/payment-methods');
      })
      .catch(err => {
        toast.error(err.response?.data?.error || 'Não foi possível inativar a forma de recebimento');
      })
      .finally(() => {
        setSaving(false);
        setConfirmDialogOpen(false);
      });
  }

  return (
    <>
      <Appbar title="Formas de recebimento" backAction={goBack} />

      {saving && <InsideSaving />}

      {confirmDialogOpen && (
        <ConfirmDialog
          confirmLabel="Inativar"
          destructive
          title="Deseja inativar esta forma de recebimento?"
          handleNoClick={() => setConfirmDialogOpen(false)}
          handleYesClick={handleInactivate}
        />
      )}

      <PageShell>
        <RecordHeader title={saved?.name || 'Forma de recebimento'} active={!!saved?.active} backAction={goBack}>
          {saved && <Chip size="small" variant="outlined" label={`Tipo ${saved.type}`} />}
          {saved && <Chip size="small" variant="outlined" label={`até ${saved.max_installments}x`} />}
        </RecordHeader>

        <WorkArea>
          <ContentPad>
            {loading ? (
              <InsideLoading />
            ) : !paymentMethod ? (
              <NoData message="Não foi possível carregar os dados da forma de recebimento" />
            ) : (
              <StyledForm onSubmit={handleValidation}>
                <PaymentMethodForm handleChange={handleChange} paymentMethod={paymentMethod} errors={errors} />
                <button type="submit" style={{ display: 'none' }}></button>
              </StyledForm>
            )}
          </ContentPad>
        </WorkArea>

        {!loading && paymentMethod && (
          <RecordFooter sx={{ justifyContent: 'space-between' }}>
            <Button color="error" size="small" disabled={saving} onClick={() => setConfirmDialogOpen(true)}>
              Inativar forma
            </Button>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="contained" size="small" disabled={saving} onClick={() => handleValidation()}>
                Salvar alterações
              </Button>
            </div>
          </RecordFooter>
        )}
      </PageShell>
    </>
  );
};

export default EditPaymentMethod;
